import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X } from 'lucide-react';
import { MOBILE_APP_CONFIG } from '@/config/constants';
import { ReferralSuccessCard } from '@/components/checkout/ReferralSuccessCard';
import type { ReferralRewardData } from '@/types/checkout';

interface OrderSuccessLocationState {
  isFirstPurchase?: boolean;
  isReferralRewardQualified?: boolean;
  referralRewardData?: ReferralRewardData | null;
}

export default function OrderSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const state = (location.state ?? {}) as OrderSuccessLocationState;
  // Show the referral card only when the server confirms:
  // 1. This is the user's first purchase AND
  // 2. They signed up via a referral invite link (isReferralRewardQualified) AND
  // 3. The server returned the reward data to display
  const showReferralCard =
    state.isFirstPurchase === true &&
    state.isReferralRewardQualified === true &&
    !!state.referralRewardData;

  const referralCode = state.referralRewardData?.refereeCode ?? '';
  const rewardAmount = state.referralRewardData?.rewardAmount ?? 500;

  if (showReferralCard) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] flex items-center justify-center p-4">
        <ReferralSuccessCard
          rewardAmount={rewardAmount}
          referralCode={referralCode}
        />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#f4f4f4] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Confetti Background Decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute" style={{ left: '30%', top: '15%' }} width="20" height="60" viewBox="0 0 20 60">
            <path d="M10 0 Q15 30 10 60" stroke="#FF6B9D" strokeWidth="3" fill="none" />
          </svg>
          <svg className="absolute" style={{ left: '35%', top: '20%' }} width="15" height="15">
            <circle cx="7.5" cy="7.5" r="7.5" fill="#4ECDC4" />
          </svg>
          <svg className="absolute" style={{ left: '40%', top: '25%' }} width="20" height="60" viewBox="0 0 20 60">
            <path d="M10 0 Q5 30 10 60" stroke="#FFE66D" strokeWidth="3" fill="none" />
          </svg>
          <svg className="absolute" style={{ left: '45%', top: '18%' }} width="12" height="12">
            <rect width="12" height="12" fill="#FF6B9D" transform="rotate(45 6 6)" />
          </svg>
          <svg className="absolute" style={{ right: '30%', top: '15%' }} width="20" height="60" viewBox="0 0 20 60">
            <path d="M10 0 Q15 30 10 60" stroke="#4ECDC4" strokeWidth="3" fill="none" />
          </svg>
          <svg className="absolute" style={{ right: '35%', top: '22%' }} width="15" height="15">
            <circle cx="7.5" cy="7.5" r="7.5" fill="#A8E6CF" />
          </svg>
          <svg className="absolute" style={{ right: '40%', top: '28%' }} width="20" height="60" viewBox="0 0 20 60">
            <path d="M10 0 Q5 30 10 60" stroke="#FF6B9D" strokeWidth="3" fill="none" />
          </svg>
          <svg className="absolute" style={{ right: '45%', top: '20%' }} width="10" height="10">
            <circle cx="5" cy="5" r="5" fill="#FFE66D" />
          </svg>
          <svg className="absolute" style={{ left: '25%', top: '12%' }} width="8" height="8">
            <rect width="8" height="8" fill="#4ECDC4" />
          </svg>
          <svg className="absolute" style={{ right: '25%', top: '16%' }} width="8" height="8">
            <rect width="8" height="8" fill="#A8E6CF" />
          </svg>
          <svg className="absolute" style={{ left: '50%', top: '10%' }} width="20" height="60" viewBox="0 0 20 60">
            <path d="M10 0 Q15 30 10 60" stroke="#A8E6CF" strokeWidth="3" fill="none" />
          </svg>
          <svg className="absolute" style={{ left: '55%', top: '24%' }} width="12" height="12">
            <circle cx="6" cy="6" r="6" fill="#FFE66D" />
          </svg>
        </div>

        {/* Success Card */}
        <div className="bg-white rounded-[10px] max-w-[510px] w-full p-12 text-center relative z-10 shadow-sm">
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <img 
              src="/success-check.svg" 
              alt="Success" 
              className="w-[90px] h-[90px] "
            />
          </div>

          {/* Heading */}
          <h1 className="font-lexend text-[30px] font-medium leading-[28px] capitalize text-black mb-6">
            Payment Successful
          </h1>

          {/* Description */}
          <p className="font-lexend text-[16px] font-light leading-[28px] capitalize text-black/60 mb-10">
            Congratulations! Your Order Was Successfully Made, And Its Being Processed.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col gap-[18px]">
            <Button
              onClick={() => navigate('/shop')}
              className="w-full h-[50px] bg-[#328852] hover:bg-[#2a7345] text-white font-lexend text-[20px] font-normal capitalize rounded-md"
            >
              Back To Home
            </Button>

            <Button
              onClick={() => setShowDownloadModal(true)}
              variant="outline"
              className="w-full h-[50px] bg-white hover:bg-gray-50 text-black border border-gray-200 font-lexend text-[20px] font-normal capitalize rounded-md"
            >
              Track Order
            </Button>
          </div>
        </div>
      </div>

      {/* Download App Modal */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent 
          className="max-w-[734px] w-[calc(100%-2rem)] sm:w-[90%] bg-[#5466b5] border-none p-0 rounded-[20px] overflow-hidden"
          showCloseButton={false}
        >
          {/* Close Button */}
          <button
            onClick={() => setShowDownloadModal(false)}
            className="absolute top-4 right-4 sm:top-7 sm:right-7 z-10 w-5 h-5 flex items-center justify-center text-white hover:opacity-80 transition-opacity"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>

          {/* Modal Content */}
          <div className="flex flex-col items-center gap-6 sm:gap-[30px] px-6 py-8 sm:px-12 sm:py-16">
            {/* Heading */}
            <div className="flex flex-col gap-6 sm:gap-[52px] items-center text-center">
              <h2 className="font-lexend text-3xl sm:text-4xl md:text-[50px] font-medium leading-tight sm:leading-[28px] capitalize text-white tracking-[-0.22px]">
                Download Our App
              </h2>
              
              <p className="font-lexend text-base sm:text-xl md:text-[25px] font-light leading-6 sm:leading-[35px] capitalize text-white tracking-[-0.22px] max-w-[522px] px-4 sm:px-0">
                To Monitor And Track Your Order, We Invite You To Download The Auqli App.
              </p>
            </div>

            {/* Store Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-[32px] items-center justify-center w-full sm:w-auto">
              <a 
                href={MOBILE_APP_CONFIG.APP_STORE.ANDROID} 
                aria-label="Get it on Google Play" 
                className="h-[49px] sm:h-[56px] xl:h-[70px] w-auto inline-flex items-center justify-center"
              >
                <img 
                  src={'../assets/PlaystoreBadge.svg'} 
                  alt="Get it on Google Play" 
                  className="h-full w-auto" 
                />
              </a>
              <a 
                href={MOBILE_APP_CONFIG.APP_STORE.IOS} 
                aria-label="Download on the App Store" 
                className="h-[49px] sm:h-[56px] xl:h-[70px] w-auto inline-flex items-center justify-center"
              >
                <img 
                  src={'../assets/iOSBadge.svg'} 
                  alt="Download on the App Store" 
                  className="h-full w-auto" 
                />
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
